<template>
  <b-card title="Пользователи">
    <div class="mb-1 d-flex justify-content-end">
      <b-button
        v-if="$can('manage', 'users')"
        variant="primary"
        @click="openCreateModal"
      >
        Добавить
      </b-button>
    </div>

    <b-table
      :items="users"
      :fields="fields"
      :busy="loading"
      striped
      responsive
      show-empty
      empty-text="Нет пользователей"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle mr-50" />
          <strong>Загрузка...</strong>
        </div>
      </template>

      <template #cell(avatar)="data">
        <b-avatar :src="data.item.avatar" :text="initials(data.item.name)" size="32" />
      </template>

      <template #cell(is_active)="data">
        <b-badge :variant="data.item.is_active ? 'light-success' : 'light-secondary'">
          {{ data.item.is_active ? 'Активен' : 'Отключён' }}
        </b-badge>
      </template>
      <template #cell(actions)="data">
        <b-button
          v-if="$can('manage', 'users')"
          size="sm"
          variant="outline-primary"
          class="mr-50"
          @click="openEditModal(data.item)"
        >
          Изменить
        </b-button>
        <b-button
          v-if="$can('manage', 'users')"
          size="sm"
          variant="outline-warning"
          @click="openResetModal(data.item)"
        >
          Сбросить пароль
        </b-button>
      </template>
    </b-table>

    <!-- Создание -->
    <b-modal
      v-model="createModalVisible"
      title="Новый пользователь"
      ok-title="Создать"
      cancel-title="Отмена"
      :ok-disabled="saving"
      @ok.prevent="createUser"
    >
      <b-form-group label="Имя">
        <b-form-input v-model="createForm.name" />
      </b-form-group>
      <b-form-group label="Email">
        <b-form-input v-model="createForm.email" type="email" />
      </b-form-group>
      <b-form-group label="Пароль (мин. 8 символов)">
        <b-form-input v-model="createForm.password" type="text" />
      </b-form-group>
      <b-form-group label="Роль">
        <b-form-select v-model="createForm.role" :options="roleOptions" />
      </b-form-group>
    </b-modal>

    <!-- Редактирование -->
    <b-modal
      v-model="editModalVisible"
      title="Редактирование пользователя"
      ok-title="Сохранить"
      cancel-title="Отмена"
      size="lg"
      :ok-disabled="saving"
      @ok.prevent="updateUser"
    >
      <!-- Аватарка сохраняется сразу, отдельным запросом — не ждёт кнопки «Сохранить» -->
      <b-form-group label="Аватарка">
        <div class="d-flex align-items-center">
          <b-avatar :src="editForm.avatar" :text="initials(editForm.name)" size="64" class="mr-1" />
          <b-form-file
            ref="avatarInput"
            accept="image/jpeg,image/png,image/webp"
            placeholder="Выбрать файл..."
            browse-text="Обзор"
            class="mr-1"
            :disabled="avatarUploading"
            @input="uploadAvatar"
          />
          <b-spinner v-if="avatarUploading" small class="mr-1" />
          <b-button
            v-if="editForm.avatar && !avatarUploading"
            size="sm"
            variant="outline-danger"
            @click="deleteAvatar"
          >
            Удалить
          </b-button>
        </div>
      </b-form-group>

      <b-form-group label="Имя">
        <b-form-input v-model="editForm.name" />
      </b-form-group>
      <b-form-group label="Email">
        <b-form-input v-model="editForm.email" type="email" />
      </b-form-group>
      <b-form-group label="Роль">
        <b-form-select v-model="editForm.role" :options="roleOptions" />
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="editForm.is_active">
          Активен
        </b-form-checkbox>
      </b-form-group>

      <b-form-group label="Прямые пермишшены (исключения поверх роли)">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Раздел</th>
              <th>Просмотр</th>
              <th>Редактирование</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="section in sections" :key="section">
              <td>{{ section }}</td>
              <td>
                <b-form-checkbox
                  :checked="editForm.direct_permissions.includes(`${section}.view`)"
                  @change="togglePermission(`${section}.view`)"
                />
              </td>
              <td>
                <b-form-checkbox
                  :checked="editForm.direct_permissions.includes(`${section}.manage`)"
                  @change="togglePermission(`${section}.manage`)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </b-form-group>
    </b-modal>

    <!-- Сброс пароля -->
    <b-modal
      v-model="resetModalVisible"
      title="Сброс пароля"
      ok-title="Сбросить"
      ok-variant="warning"
      cancel-title="Отмена"
      :ok-disabled="saving"
      @ok.prevent="resetPassword"
    >
      <p>
        Пользователь <strong>{{ resetTarget && resetTarget.name }}</strong> будет разлогинен на всех устройствах.
      </p>
      <b-form-group label="Новый пароль (мин. 8 символов)">
        <b-form-input v-model="resetForm.password" type="text" />
      </b-form-group>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BButton, BBadge, BModal, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BSpinner,
  BAvatar, BFormFile,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard, BTable, BButton, BBadge, BModal, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BSpinner,
  BAvatar, BFormFile,
  },
  data() {
    return {
      loading: false,
      saving: false,
      users: [],
      roles: [],
      sections: [],
      fields: [
        { key: 'avatar', label: '', class: 'align-middle' },
        { key: 'name', label: 'Имя', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'role', label: 'Роль', sortable: true },
        { key: 'is_active', label: 'Статус' },
        { key: 'created_at', label: 'Создан', sortable: true },
        { key: 'actions', label: '' },
      ],
      createModalVisible: false,
      createForm: { name: '', email: '', password: '', role: 'user' },
      editModalVisible: false,
      editTargetId: null,
      editForm: { name: '', email: '', role: 'user', is_active: true, avatar: null, direct_permissions: [] },
      avatarUploading: false,
      resetModalVisible: false,
      resetTarget: null,
      resetForm: { password: '' },
    }
  },
  computed: {
    roleOptions() {
      return this.roles.map(role => ({ value: role.name, text: role.name }))
    },
  },
  created() {
    this.loadUsers()
    this.loadReference()
  },
  methods: {
    loadUsers() {
      this.loading = true
      this.$http.get('/users')
        .then(response => { this.users = response.data })
        .finally(() => { this.loading = false })
    },
    loadReference() {
      this.$http.get('/roles').then(response => { this.roles = response.data })
      this.$http.get('/permissions').then(response => { this.sections = response.data.sections })
    },
    openCreateModal() {
      this.createForm = { name: '', email: '', password: '', role: 'user' }
      this.createModalVisible = true
    },
    createUser() {
      this.saving = true
      this.$http.post('/users', this.createForm)
        .then(() => {
          this.createModalVisible = false
          this.notify('Пользователь создан')
          this.loadUsers()
        })
        .catch(error => this.notifyError(error))
        .finally(() => { this.saving = false })
    },
    openEditModal(user) {
      this.editTargetId = user.id
      this.editForm = {
        name: user.name,
        email: user.email,
        role: user.role,
        is_active: user.is_active,
        avatar: user.avatar,
        direct_permissions: [...user.direct_permissions],
      }
      this.editModalVisible = true
    },
    initials(name) {
      return (name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0].toUpperCase())
        .join('')
    },
    uploadAvatar(file) {
      if (!file) return
      const formData = new FormData()
      formData.append('file', file)
      this.avatarUploading = true
      this.$http.post(`/users/${this.editTargetId}/avatar`, formData)
        .then(response => {
          this.editForm.avatar = response.data.avatar
          this.notify('Аватарка обновлена')
          this.loadUsers()
        })
        .catch(error => this.notifyError(error))
        .finally(() => {
          this.avatarUploading = false
          // Сбрасываем инпут, иначе повторный выбор того же файла не сработает
          if (this.$refs.avatarInput) this.$refs.avatarInput.reset()
        })
    },
    deleteAvatar() {
      this.avatarUploading = true
      this.$http.delete(`/users/${this.editTargetId}/avatar`)
        .then(() => {
          this.editForm.avatar = null
          this.notify('Аватарка удалена')
          this.loadUsers()
        })
        .catch(error => this.notifyError(error))
        .finally(() => { this.avatarUploading = false })
    },
    togglePermission(permissionName) {
      const index = this.editForm.direct_permissions.indexOf(permissionName)
      if (index === -1) {
        this.editForm.direct_permissions.push(permissionName)
      } else {
        this.editForm.direct_permissions.splice(index, 1)
      }
    },
    updateUser() {
      this.saving = true
      this.$http.put(`/users/${this.editTargetId}`, this.editForm)
        .then(() => {
          this.editModalVisible = false
          this.notify('Сохранено')
          this.loadUsers()
        })
        .catch(error => this.notifyError(error))
        .finally(() => { this.saving = false })
    },
    openResetModal(user) {
      this.resetTarget = user
      this.resetForm = { password: '' }
      this.resetModalVisible = true
    },
    resetPassword() {
      this.saving = true
      this.$http.post(`/users/${this.resetTarget.id}/reset-password`, this.resetForm)
        .then(() => {
          this.resetModalVisible = false
          this.notify('Пароль сброшен')
        })
        .catch(error => this.notifyError(error))
        .finally(() => { this.saving = false })
    },
    notify(title) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'CheckIcon', variant: 'success' },
      })
    },
    notifyError(error) {
      const message = (error.response && error.response.data && (error.response.data.message || JSON.stringify(error.response.data.errors))) || 'Ошибка'
      this.$toast({
        component: ToastificationContent,
        props: { title: message, icon: 'AlertTriangleIcon', variant: 'danger' },
      })
    },
  },
}
</script>
